import { styled } from '@mui/material/styles';
import { Landing } from '../Landing/index';
import { type LandingPageType } from '@Src/middlewares/CustomPageTypes';
import Highlight from '../Highlight';
import { OrderSettings } from '@Components/Widgets/OrderSettings';
import Apps from '@Components/Widgets/Apps';
import MenuHighlights from '../Menu';
import Testimonials from '@Components/Widgets/Testimonials';
import Gallery from '@Components/Widgets/Gallery';
import OrderStatus from '@Components/Widgets/OrderStatus';
import Reorder from '@Components/Widgets/Reorder';
import { LogInTextButton } from '../LogIn';
import { useAppSelector } from '@Src/hooks/useAppSelector';
import LocationsWidget from '@Components/Widgets/Locations';
import { useTranslation } from '@Src/providers/TranslationProvider';
import { useDesignSchema } from '@Src/providers/DesignSchemaProvider';
import { StyledPaddedSection } from '../Common/StyledSection';
import Voucher from '@Components/Widgets/Voucher';
import Grid from '@mui/material/Grid';

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing(9)};
`;

const StyledLandingChildrenWrapper = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

const StyledOrderSettingsContainer = styled('div')<{
  ownerState: { isCollection: boolean };
}>(({ ownerState, theme }) => ({
  width: '100%',
  minHeight: '200px',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    minHeight: '120px',
    flexDirection: ownerState.isCollection ? 'column' : 'row',
    maxWidth: 'calc(1640px - 64px)',
    marginTop: theme.spacing(-13.5),
    paddingLeft: theme.spacing(14),
    paddingRight: theme.spacing(14),
    position: 'relative',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(-3.5),
  },
}));

const StyledLogInContainer = styled('div')<{
  ownerState: { isCollection: boolean };
}>(({ ownerState, theme }) => ({
  alignItems: ownerState.isCollection ? 'center' : 'flex-end',
  display: 'flex',
  flexDirection: 'column-reverse',
  marginTop: ownerState.isCollection ? theme.spacing(4) : 0,
  width: ownerState.isCollection ? '100%' : '55%',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: theme.spacing(4),
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: 0,
  },
}));

const StyledReorderContainer = styled('div')`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding-left: ${({ theme }) => theme.spacing(19)};
    padding-right: ${({ theme }) => theme.spacing(14)};
    max-width: calc(1640px - 64px);
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`;

const StyledTestimonials = styled(Testimonials)`
  padding-bottom: 0 !important;
`;

const StyledGallery = styled(Gallery)`
  padding-top: ${({ theme }) => theme.spacing(9)} !important;
  padding-bottom: 0 !important;
`;

const StyledLocationsWrapper = styled(StyledPaddedSection)`
  padding-top: ${({ theme }) => theme.spacing(9)} !important;
  padding-bottom: 0 !important;
`;

const StyledApps = styled(Apps)`
  padding-top: ${({ theme }) => theme.spacing(9)} !important;
  padding-bottom: ${({ theme }) => theme.spacing(1.25)} !important;
`;

const StyledHighlight = styled(Highlight)`
  padding-top: ${({ theme }) => theme.spacing(9)};
  padding-bottom: 0 !important;
`;

export const V2Template = ({
  data,
}: {
  data: LandingPageType;
}): JSX.Element => {
  const isLoggedIn = useAppSelector((state) => state.user.isLoggedIn);
  const { translate } = useTranslation();
  const { storeMapData, voucher } = useDesignSchema('v2');
  const isCollection = useAppSelector(
    (state) => state.orderSettings.isCollection,
  );

  return (
    <StyledContainer>
      <Landing
        brandNameHeadingEnabled={data?.brandNameHeadingEnabled}
        backgroundImages={data?.backgroundImages}
        loggedInHeading={data?.loggedInHeading}
        loggedOutHeading={data?.loggedOutHeading}
      >
        <StyledLandingChildrenWrapper>
          <OrderStatus />
          <Voucher data={voucher} />
        </StyledLandingChildrenWrapper>
      </Landing>
      <StyledOrderSettingsContainer
        ownerState={{
          isCollection,
        }}
      >
        <OrderSettings />
        {!isLoggedIn && (
          <StyledLogInContainer ownerState={{ isCollection }}>
            <LogInTextButton />
          </StyledLogInContainer>
        )}
      </StyledOrderSettingsContainer>
      <StyledReorderContainer>
        <Reorder />
      </StyledReorderContainer>
      <MenuHighlights />
      {data.highlight?.enabled && <StyledHighlight data={data?.highlight} />}
      {data.testimonialsEnabled && <StyledTestimonials />}
      {data.mobileAppsEnabled && <StyledApps />}
      {data.galleryEnabled && <StyledGallery />}
      {data.locationsEnabled && (
        <StyledLocationsWrapper>
          <LocationsWidget
            title={translate('Locations')}
            subTitle={
              storeMapData?.length
                ? `${storeMapData?.length} ${translate(
                    storeMapData?.length > 1 ? 'stores' : 'store',
                  )}`
                : ''
            }
          />
        </StyledLocationsWrapper>
      )}
    </StyledContainer>
  );
};

export default V2Template;
